import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { EditorContext, SiteContext } from '../../../../utils/contexts';
import useGamification from './useGamification';
import CollapsedPanel from '../../components/CollapsedPanel';
import InputBlock from '../../components/InputBlock';
import ColorPicker from '../../components/ColorPicker';
import NumberInput from '../../components/NumberInput';
import Switcher from '../../components/Switcher';
import SelectInput from '../../components/SelectInput';
import Dropzone from '../../components/Dropzone';
import useAPI from '../../../../utils/api';
import { getGamificationThemeDefaults, resolveGamificationColor } from './gamificationThemeDefaults';
import { FONT_FAMILIES } from '../../constants';

const TabSection = styled.div`
  padding: 4px 0 12px;
`;

const TabLabel = styled.div`
  font-weight: bold;
  font-size: 0.8em;
  margin-bottom: 8px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 8px;
`;

const UploadNote = styled.p`
  font-size: 0.78em;
  color: #888;
  background: #f9f5ec;
  border-left: 3px solid #e9562b;
  padding: 6px 8px;
  margin: 0 0 10px;
  line-height: 1.4;
`;

const Tab = styled.button`
  flex: 1;
  padding: 7px 0;
  border-radius: 20px;
  border: 2px solid ${(p) => (p.$active ? '#e9562b' : '#e2e2e2')};
  background: ${(p) => (p.$active ? '#fff' : '#f5f5f5')};
  color: ${(p) => (p.$active ? '#e9562b' : '#666')};
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  font-size: 0.88em;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover {
    border-color: #e9562b;
    color: #e9562b;
  }
`;


const LibraryScrollBox = styled.div`
  height: 160px;
  overflow-y: auto;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  padding: 4px;
  margin-top: 4px;
`;

const LibraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 6px;
`;

const LibraryThumb = styled.div`
  position: relative;
  border: 2px solid ${(p) => (p.$selected ? '#e9562b' : 'transparent')};
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;

  img {
    width: 100%;
    height: 55px;
    object-fit: cover;
    display: block;
  }

  &:hover {
    border-color: #e9562b;
  }
`;

const LibraryLabel = styled.div`
  font-size: 0.75em;
  color: #888;
  margin: 10px 0 4px;
`;

const ElementListCard = styled.div`
  background: #fafafa;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  padding: 12px;
  margin: 10px 0 18px;
`;

const ElementListHeader = styled.div`
  font-weight: 600;
  font-size: 0.82em;
  color: #444;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ElementRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid ${(p) => (p.$hidden ? '#eeeeee' : '#e2e2e2')};
  border-radius: 6px;
  margin-bottom: 6px;
  opacity: ${(p) => (p.$hidden ? 0.6 : 1)};
  transition: all 0.15s ease;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ElementTitle = styled.span`
  font-size: 0.84em;
  font-weight: 500;
  color: ${(p) => (p.$hidden ? '#999999' : '#222222')};
  text-decoration: ${(p) => (p.$hidden ? 'line-through' : 'none')};
`;

const ElementActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MoveBtn = styled.button`
  background: #f4f4f5;
  border: 1px solid #d4d4d8;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75em;
  color: #3f3f46;
  cursor: pointer;
  transition: background 0.12s;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  &:hover:not(:disabled) {
    background: #e4e4e7;
    color: #18181b;
  }
`;

function ResultElementManager({ gamification, updateGamification }) {
  const defaultOrder = ['icon', 'headline', 'caption', 'reward_line', 'code_box', 'cta_button'];
  const elementLabels = {
    icon: 'Icon / Emoji',
    headline: 'Headline',
    caption: 'Caption / Supporting Text',
    reward_line: '"You won X" Prize Line',
    code_box: 'Coupon Code Box',
    cta_button: 'CTA Button',
  };

  const rawOrder = gamification.result_element_order || defaultOrder;
  const order = [...rawOrder, ...defaultOrder.filter(k => !rawOrder.includes(k))];
  const hidden = new Set(gamification.result_hidden_elements || []);

  const moveElement = (index, direction) => {
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(index, 1);
    next.splice(targetIdx, 0, moved);
    updateGamification({ result_element_order: next });
  };

  const toggleVisibility = (key, e) => {
    const nextHidden = new Set(hidden);
    if (e.target.checked) {
      nextHidden.delete(key);
    } else {
      nextHidden.add(key);
    }
    updateGamification({ result_hidden_elements: Array.from(nextHidden) });
  };

  return (
    <ElementListCard>
      <ElementListHeader>Result Screen Elements (Order & Visibility)</ElementListHeader>
      {order.map((key, idx) => {
        const isVisible = !hidden.has(key);
        return (
          <ElementRow key={key} $hidden={!isVisible}>
            <ElementTitle $hidden={!isVisible}>{elementLabels[key] || key}</ElementTitle>
            <ElementActions>
              <MoveBtn
                type="button"
                onClick={() => moveElement(idx, -1)}
                disabled={idx === 0}
                title="Move Up"
              >
                ▲
              </MoveBtn>
              <MoveBtn
                type="button"
                onClick={() => moveElement(idx, 1)}
                disabled={idx === order.length - 1}
                title="Move Down"
              >
                ▼
              </MoveBtn>
              <Switcher
                checked={isVisible}
                onChange={(e) => toggleVisibility(key, e)}
              />
            </ElementActions>
          </ElementRow>
        );
      })}
    </ElementListCard>
  );
}

function ImageLibrary({ images, selectedUrl, onSelect }) {
  if (!images || images.length === 0) return null;
  return (
    <>
      <LibraryLabel>Your Library</LibraryLabel>
      <LibraryScrollBox>
        <LibraryGrid>
          {images.map((img) => (
            <LibraryThumb
              key={img.id}
              $selected={selectedUrl === img.url}
              onClick={() => onSelect(img)}
              title="Use this image"
            >
              <img src={img.modal_url || img.url} alt="" />
            </LibraryThumb>
          ))}
        </LibraryGrid>
      </LibraryScrollBox>
    </>
  );
}

function GamificationPanel() {
  const { gamification, updateGamification } = useGamification();
  const { settings } = useContext(EditorContext);
  const site = useContext(SiteContext);
  const API = useAPI();
  const [boxImageLoading, setBoxImageLoading] = useState(false);
  const [scratchOverlayImageLoading, setScratchOverlayImageLoading] = useState(false);
  const [centerLogoLoading, setCenterLogoLoading] = useState(false);
  const [logoLibrary, setLogoLibrary] = useState([]);
  const [lootboxLibrary, setLootboxLibrary] = useState([]);
  const [scratchLibrary, setScratchLibrary] = useState([]);

  useEffect(() => {
    API.getImageLibrary('logo').then(([ok, data]) => { if (ok) setLogoLibrary(data.image_uploads || []); });
    API.getImageLibrary('lootbox').then(([ok, data]) => { if (ok) setLootboxLibrary(data.image_uploads || []); });
    API.getImageLibrary('scratch_overlay').then(([ok, data]) => { if (ok) setScratchLibrary(data.image_uploads || []); });
  }, []);

  const boxImageMode = gamification.box_image_mode || 'default';
  const scratchOverlayImageMode = gamification.scratch_overlay_image_mode || 'default';

  const handleBoxImageUpload = (_, file) => {
    setBoxImageLoading(true);
    API.uploadImage(API.siteId, file, 'lootbox')
      .then(([, json]) => {
        setBoxImageLoading(false);
        setLootboxLibrary((prev) => [...prev, json]);
        updateGamification({ box_image_url: json.url, box_style: 'image' });
      })
      .catch(() => setBoxImageLoading(false));
  };

  const handleBoxImageDelete = () => {
    updateGamification({ box_image_url: null, box_style: gamification.box_style === 'image' ? 'style1' : gamification.box_style });
  };

  const handleScratchOverlayImageUpload = (_, file) => {
    setScratchOverlayImageLoading(true);
    API.uploadImage(API.siteId, file, 'scratch_overlay')
      .then(([, json]) => {
        setScratchOverlayImageLoading(false);
        setScratchLibrary((prev) => [...prev, json]);
        updateGamification({ scratch_overlay_image_url: json.url, scratch_overlay_image_mode: 'upload' });
      })
      .catch(() => setScratchOverlayImageLoading(false));
  };

  const handleScratchOverlayImageDelete = () => {
    updateGamification({ scratch_overlay_image_url: null, scratch_overlay_image_mode: 'default' });
  };

  const handleCenterLogoUpload = (_, file) => {
    setCenterLogoLoading(true);
    API.uploadImage(API.siteId, file, 'logo')
      .then(([, json]) => {
        setCenterLogoLoading(false);
        setLogoLibrary((prev) => [...prev, json]);
        updateGamification({ center_logo_url: json.url });
      })
      .catch(() => setCenterLogoLoading(false));
  };

  const handleCenterLogoDelete = () => {
    updateGamification({ center_logo_url: null });
  };

  const gameType = gamification.game_type || 'spin_wheel';
  const isScratchCard = gameType === 'scratch_card';
  const isLootBox = gameType === 'surprise_box';
  const isSpinWheel = !isScratchCard && !isLootBox;

  const defaults = getGamificationThemeDefaults(settings.theme_id);
  const spinDefaults = { ...defaults.spin };
  if (gamification.wheel_style === 'lucky') {
    spinDefaults.outerRingColor = 'FFFFFF';
    spinDefaults.dotsColor = '407596';
    spinDefaults.centerColor = 'FFFFFF';
    spinDefaults.centerRadius = 35;
  } else if (gamification.wheel_style === 'casino') {
    spinDefaults.outerRingColor = '1D1D1B';
    spinDefaults.dotsColor = 'F8E219';
    spinDefaults.centerColor = 'E51C24';
    spinDefaults.centerRadius = 35;
    spinDefaults.pointerColor = 'E51C24';
  }
  const scratchDefaults = defaults.scratch;
  const lootBoxDefaults = defaults.lootBox;

  const defaultCenterRadius = spinDefaults.centerRadius ?? 10;
  const centerRadiusValue = Number.isFinite(Number(gamification.center_blank_radius))
    ? Number(gamification.center_blank_radius)
    : defaultCenterRadius;
  const pointerColorValue = resolveGamificationColor(gamification.pointer_color, spinDefaults.pointerColor);
  const centerColorValue = resolveGamificationColor(gamification.decorations?.centerColor, spinDefaults.centerColor);
  const outerRingColorValue = resolveGamificationColor(gamification.decorations?.outerRingColor, spinDefaults.outerRingColor);
  const dotsColorValue = resolveGamificationColor(gamification.decorations?.dotsColor, spinDefaults.dotsColor);

  const scratchOverlayColorValue = resolveGamificationColor(gamification.scratch_overlay_color, scratchDefaults.scratchOverlayColor);
  const scratchBgColorValue = resolveGamificationColor(gamification.scratch_bg_color, scratchDefaults.scratchBgColor);

  const couponBoxBgColorValue = resolveGamificationColor(gamification.coupon_box_bg_color, 'ffffff');
  const couponBoxTextColorValue = resolveGamificationColor(gamification.coupon_box_text_color, '0F172A');

  const wheelPlacementOptions = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
  ];

  const wheelStyleOptions = [
    { value: 'default', label: 'Default' },
    { value: 'lucky', label: 'Lucky Wheel' },
    { value: 'casino', label: 'Casino Wheel' },
  ];

  const boxCountOptions = [
    { value: 3, label: '3 Boxes' },
    { value: 6, label: '6 Boxes' },
    { value: 9, label: '9 Boxes' },
  ];

  const resultLayoutOptions = [
    { value: 'stacked', label: 'Stacked (centered)' },
    { value: 'split', label: 'Side by side' },
  ];

  const couponBorderStyleOptions = [
    { value: 'dashed', label: 'Dashed' },
    { value: 'solid', label: 'Solid' },
    { value: 'none', label: 'None' },
  ];

  const ctaFontFamilyOptions = [
    { value: '', label: 'Theme default' },
    ...Object.entries(FONT_FAMILIES).map(([value, label]) => ({ value, label })),
  ];

  const revealAnimationOptions = [
    { value: 'staggered', label: 'Staggered' },
    { value: 'simple', label: 'All at once' },
  ];

  const confettiOptions = [
    { value: 'auto', label: 'On' },
    { value: 'off', label: 'Off' },
  ];

  const panelTitle = isScratchCard ? 'Card Styling' : isLootBox ? 'Box Styling' : 'Wheel Styling';

  return (
    <>
    <CollapsedPanel title={panelTitle} open={false} allowed>

      <ElementListCard>
        <ElementListHeader>General</ElementListHeader>
        <InputBlock label="One play per visitor">
          <Switcher
            checked={!!gamification.one_play_per_visitor}
            onChange={(e) => updateGamification({ one_play_per_visitor: e.target.checked })}
          />
        </InputBlock>
      </ElementListCard>

      {/* ── Spin wheel-only customization ───────────────────────────────── */}
      {isSpinWheel && (
        <>
          <ElementListCard>
            <ElementListHeader>Pointer &amp; Center</ElementListHeader>
            <InputBlock label="Pointer color">
              <ColorPicker
                value={pointerColorValue}
                onChange={(color) => updateGamification({ pointer_color: color })}
              />
            </InputBlock>

            <InputBlock label="Center Color">
              <ColorPicker
                value={centerColorValue}
                onChange={(color) =>
                  updateGamification({
                    decorations: {
                      ...(gamification.decorations || {}),
                      centerColor: color,
                    },
                  })
                }
              />
            </InputBlock>

            <InputBlock label="Center Radius">
              <NumberInput
                value={centerRadiusValue}
                onChange={(v) => updateGamification({ center_blank_radius: v })}
                min={0}
                max={50}
                size="small"
              />
            </InputBlock>

            <TabSection>
              <TabLabel>Center Logo</TabLabel>
              <Dropzone
                settings={{
                  active_image_id: gamification.center_logo_url ? 'custom' : null,
                  image_file_name: gamification.center_logo_url ? 'Center logo' : null,
                }}
                onDelete={handleCenterLogoDelete}
                onUpdate={handleCenterLogoUpload}
                loading={centerLogoLoading}
              />
              <ImageLibrary
                images={logoLibrary}
                selectedUrl={gamification.center_logo_url}
                onSelect={(img) => updateGamification({ center_logo_url: img.url })}
              />
            </TabSection>
          </ElementListCard>

          <ElementListCard>
            <ElementListHeader>Wheel Size &amp; Placement</ElementListHeader>
            <InputBlock label="Wheel size (px)">
              <NumberInput
                value={gamification.wheel_size ?? 350}
                onChange={(v) => updateGamification({ wheel_size: v })}
                min={100}
                max={1200}
                size="small"
              />
            </InputBlock>

            <InputBlock label="Wheel placement">
              <SelectInput
                items={wheelPlacementOptions}
                value={gamification.wheel_placement || 'left'}
                onChange={(value) => updateGamification({ wheel_placement: value })}
              />
            </InputBlock>

            <InputBlock label="Wheel Style">
              <SelectInput
                items={wheelStyleOptions}
                value={gamification.wheel_style || 'default'}
                onChange={(value) => updateGamification({ wheel_style: value })}
              />
            </InputBlock>
          </ElementListCard>

          <ElementListCard>
            <ElementListHeader>Outer Ring</ElementListHeader>
            <InputBlock label="Show outer ring">
              <Switcher
                checked={gamification.decorations?.outerRing !== false}
                onChange={(e) =>
                  updateGamification({
                    decorations: {
                      ...(gamification.decorations || {}),
                      outerRing: e.target.checked,
                    },
                  })
                }
              />
            </InputBlock>

            {(gamification.decorations?.outerRing !== false) && (
              <>
                <InputBlock label="Ring Color">
                  <ColorPicker
                    value={outerRingColorValue}
                    onChange={(color) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          outerRingColor: color,
                        },
                      })
                    }
                  />
                </InputBlock>
                <InputBlock label="Ring Width">
                  <NumberInput
                    value={gamification.decorations?.outerRingWidth ?? 10}
                    onChange={(v) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          outerRingWidth: v,
                        },
                      })
                    }
                    min={0}
                    max={100}
                    size="small"
                  />
                </InputBlock>
                <InputBlock label="Ring offset">
                  <NumberInput
                    value={gamification.decorations?.outerRingRadius ?? 0}
                    onChange={(v) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          outerRingRadius: v,
                        },
                      })
                    }
                    min={0}
                    max={1000}
                    size="small"
                  />
                </InputBlock>
              </>
            )}
          </ElementListCard>

          <ElementListCard>
            <ElementListHeader>Dot Markers</ElementListHeader>
            <InputBlock label="Show dot markers">
              <Switcher
                checked={gamification.decorations?.dots !== false}
                onChange={(e) =>
                  updateGamification({
                    decorations: {
                      ...(gamification.decorations || {}),
                      dots: e.target.checked,
                    },
                  })
                }
              />
            </InputBlock>

            {(gamification.decorations?.dots !== false) && (
              <>
                <InputBlock label="Dots Color">
                  <ColorPicker
                    value={dotsColorValue}
                    onChange={(color) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          dotsColor: color,
                        },
                      })
                    }
                  />
                </InputBlock>
                <InputBlock label="Dots Size">
                  <NumberInput
                    value={gamification.decorations?.dotsSize ?? 5}
                    onChange={(v) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          dotsSize: v,
                        },
                      })
                    }
                    min={0}
                    max={40}
                    size="small"
                  />
                </InputBlock>
                <InputBlock label="Dots Count">
                  <NumberInput
                    value={gamification.decorations?.dotsCount ?? 12}
                    onChange={(v) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          dotsCount: v,
                        },
                      })
                    }
                    min={0}
                    max={100}
                    size="small"
                  />
                </InputBlock>
                <InputBlock label="Dots offset">
                  <NumberInput
                    value={gamification.decorations?.dotsRadius ?? 0}
                    onChange={(v) =>
                      updateGamification({
                        decorations: {
                          ...(gamification.decorations || {}),
                          dotsRadius: v,
                        },
                      })
                    }
                    min={0}
                    max={1000}
                    size="small"
                  />
                </InputBlock>
              </>
            )}
          </ElementListCard>
        </>
      )}

      {/* ── Scratch card-only customization ─────────────────────────────── */}
      {isScratchCard && (
        <>
          <ElementListCard>
            <ElementListHeader>Card Size &amp; Shape</ElementListHeader>
            <InputBlock label="Scratch card size (px)">
              <NumberInput
                value={gamification.scratch_card_size ?? scratchDefaults.scratchCardSize}
                onChange={(v) => updateGamification({ scratch_card_size: v })}
                min={100}
                max={800}
                size="small"
              />
            </InputBlock>

            <InputBlock label="Scratch card shape">
              <SelectInput
                items={[
                  { value: 'rect', label: 'Rectangle' },
                  { value: 'rounded-rect', label: 'Rounded Rectangle' },
                  { value: 'square', label: 'Square' },
                  { value: 'circle', label: 'Circle' },
                  { value: 'oval', label: 'Oval' },
                ]}
                value={gamification.scratch_card_shape || scratchDefaults.scratchCardShape}
                onChange={(value) => updateGamification({ scratch_card_shape: value })}
                size="small"
              />
            </InputBlock>

            <InputBlock label="Scratch area background">
              <ColorPicker
                value={scratchBgColorValue}
                onChange={(color) => updateGamification({ scratch_bg_color: color })}
              />
            </InputBlock>
          </ElementListCard>

          <ElementListCard>
            <ElementListHeader>Scratch Overlay</ElementListHeader>
            <TabSection>
              <TabLabel>Overlay Image</TabLabel>
              <TabRow>
                <Tab
                  $active={scratchOverlayImageMode === 'default'}
                  onClick={() => updateGamification({ scratch_overlay_image_mode: 'default' })}
                >
                  Default
                </Tab>
                <Tab
                  $active={scratchOverlayImageMode === 'upload'}
                  onClick={() => updateGamification({ scratch_overlay_image_mode: 'upload' })}
                >
                  Upload
                </Tab>
              </TabRow>
            </TabSection>

            {scratchOverlayImageMode === 'default' && (
              <>
                <InputBlock label="Overlay style">
                  <SelectInput
                    items={[
                      { value: 'silver', label: 'Silver Metallic' },
                      { value: 'gold', label: 'Gold Foil' },
                      { value: 'holographic', label: 'Holographic' },
                      { value: 'night_sky', label: 'Night Sky' },
                      { value: 'rose_gold', label: 'Rose Gold' },
                    ]}
                    value={gamification.scratch_overlay_style || 'silver'}
                    onChange={(value) => updateGamification({ scratch_overlay_style: value })}
                    size="small"
                  />
                </InputBlock>
                <InputBlock label="Overlay color">
                  <ColorPicker
                    value={scratchOverlayColorValue}
                    onChange={(color) => updateGamification({ scratch_overlay_color: color })}
                  />
                </InputBlock>
              </>
            )}

            {scratchOverlayImageMode === 'upload' && (
              <>
                <UploadNote>
                  Note: Overlay color doesn't apply when a custom image is uploaded.
                </UploadNote>
                <TabLabel>Custom overlay image</TabLabel>
                <Dropzone
                  settings={{
                    active_image_id: gamification.scratch_overlay_image_url ? 'custom' : null,
                    image_file_name: gamification.scratch_overlay_image_url ? 'Custom overlay image' : null,
                  }}
                  onDelete={handleScratchOverlayImageDelete}
                  onUpdate={handleScratchOverlayImageUpload}
                  loading={scratchOverlayImageLoading}
                />
                <ImageLibrary
                  images={scratchLibrary}
                  selectedUrl={gamification.scratch_overlay_image_url}
                  onSelect={(img) => updateGamification({ scratch_overlay_image_url: img.url, scratch_overlay_image_mode: 'upload' })}
                />
              </>
            )}
          </ElementListCard>
        </>
      )}

      {/* ── Loot box-only customization ──────────────────────────────────── */}
      {isLootBox && (
        <>
          <ElementListCard>
            <ElementListHeader>Box Setup</ElementListHeader>
            <InputBlock label="Number of boxes">
              <SelectInput
                items={boxCountOptions}
                value={gamification.box_count ?? lootBoxDefaults.boxCount}
                onChange={(value) => updateGamification({ box_count: Number(value) })}
              />
            </InputBlock>

            <InputBlock label="Box size (px)">
              <NumberInput
                value={gamification.box_size ?? lootBoxDefaults.boxSize}
                onChange={(v) => updateGamification({ box_size: v })}
                min={60}
                max={300}
                size="small"
              />
            </InputBlock>

            <InputBlock label="Open animation">
              <SelectInput
                items={[
                  { value: 'none', label: 'None' },
                  { value: 'bounce', label: 'Bounce' },
                  { value: 'flip', label: 'Flip' },
                  { value: 'shake', label: 'Shake' },
                  { value: 'pop', label: 'Pop' },
                ]}
                value={gamification.box_animation || 'bounce'}
                onChange={(value) => updateGamification({ box_animation: value })}
              />
            </InputBlock>
          </ElementListCard>

          <ElementListCard>
            <ElementListHeader>Box Appearance</ElementListHeader>
            <TabSection>
              <TabLabel> Box Image</TabLabel>
              <TabRow>
                <Tab
                  $active={boxImageMode === 'default'}
                  onClick={() => updateGamification({ box_image_mode: 'default' })}
                >
                  Default
                </Tab>
                <Tab
                  $active={boxImageMode === 'upload'}
                  onClick={() => updateGamification({ box_image_mode: 'upload' })}
                >
                  Upload
                </Tab>
              </TabRow>
            </TabSection>

            {boxImageMode === 'default' && (
              <>
                <InputBlock label="Box style">
                  <SelectInput
                    items={[
                      { value: 'style1', label: 'Classic Gift' },
                      { value: 'style2', label: 'Spotted Egg' },
                      { value: 'style3', label: 'Mystery Egg' },
                      { value: 'style4', label: 'Square Box' },
                      { value: 'style5', label: 'Monster Bow' },
                      { value: 'style6', label: 'Square Bow' },
                    ]}
                    value={gamification.box_style || 'style1'}
                    onChange={(value) => updateGamification({ box_style: value })}
                  />
                </InputBlock>

                <InputBlock label="Box color">
                  <ColorPicker
                    value={resolveGamificationColor(gamification.box_color, lootBoxDefaults.boxColor)}
                    onChange={(color) => updateGamification({ box_color: color })}
                  />
                </InputBlock>

                <InputBlock label="Ribbon color">
                  <ColorPicker
                    value={resolveGamificationColor(gamification.ribbon_color, lootBoxDefaults.ribbonColor)}
                    onChange={(color) => updateGamification({ ribbon_color: color })}
                  />
                </InputBlock>

              </>
            )}

            {boxImageMode === 'upload' && (
              <>
                <UploadNote>
                  Note: Box and ribbon colors don't apply to uploaded images.
                </UploadNote>
                <TabLabel>Custom box image</TabLabel>
                <Dropzone
                  settings={{
                    active_image_id: gamification.box_image_url ? 'custom' : null,
                    image_file_name: gamification.box_image_url ? 'Custom box image' : null,
                  }}
                  onDelete={handleBoxImageDelete}
                  onUpdate={handleBoxImageUpload}
                  loading={boxImageLoading}
                />
                <ImageLibrary
                  images={lootboxLibrary}
                  selectedUrl={gamification.box_image_url}
                  onSelect={(img) => updateGamification({ box_image_url: img.url, box_style: 'image' })}
                />
              </>
            )}
          </ElementListCard>
        </>
      )}

    </CollapsedPanel>

    <CollapsedPanel title="Coupon & Reward Styling" open={false} allowed>
      <ElementListCard>
        <ElementListHeader>Reward Color</ElementListHeader>
        <InputBlock label="Reward color">
          <ColorPicker
            value={resolveGamificationColor(gamification.reward_color, settings.button_color)}
            onChange={(color) => updateGamification({ reward_color: color })}
          />
        </InputBlock>
      </ElementListCard>

      <ElementListCard>
        <ElementListHeader>Coupon Code Box</ElementListHeader>
        <InputBlock label="Coupon box background color">
          <ColorPicker
            value={couponBoxBgColorValue}
            onChange={(color) => updateGamification({ coupon_box_bg_color: color })}
          />
        </InputBlock>
        <InputBlock label="Coupon code text color">
          <ColorPicker
            value={couponBoxTextColorValue}
            onChange={(color) => updateGamification({ coupon_box_text_color: color })}
          />
        </InputBlock>
        <InputBlock label="Coupon box border color">
          <ColorPicker
            value={resolveGamificationColor(gamification.coupon_box_border_color, settings.button_color || '2E86C1')}
            onChange={(color) => updateGamification({ coupon_box_border_color: color })}
          />
        </InputBlock>
        <InputBlock label="Coupon box border style">
          <SelectInput
            items={couponBorderStyleOptions}
            value={gamification.coupon_box_border_style || 'dashed'}
            onChange={(value) => updateGamification({ coupon_box_border_style: value })}
          />
        </InputBlock>
        <InputBlock label="Coupon box border width (px)">
          <NumberInput
            value={gamification.coupon_box_border_width ?? 2}
            onChange={(v) => updateGamification({ coupon_box_border_width: v })}
            min={0}
            max={10}
            size="small"
          />
        </InputBlock>
        <InputBlock label="Coupon box corner radius (px)">
          <NumberInput
            value={gamification.coupon_box_border_radius ?? 8}
            onChange={(v) => updateGamification({ coupon_box_border_radius: v })}
            min={0}
            max={40}
            size="small"
          />
        </InputBlock>
        <InputBlock label="Coupon box inner padding (px)">
          <NumberInput
            value={gamification.coupon_box_padding ?? 12}
            onChange={(v) => updateGamification({ coupon_box_padding: v })}
            min={0}
            max={40}
            size="small"
          />
        </InputBlock>
        <InputBlock label="Coupon code letter spacing (0.01em units)">
          <NumberInput
            value={gamification.coupon_box_letter_spacing ?? 12}
            onChange={(v) => updateGamification({ coupon_box_letter_spacing: v })}
            min={0}
            max={40}
            size="small"
          />
        </InputBlock>
        <InputBlock label="Coupon code font size (px)">
          <NumberInput
            value={gamification.result_code_box_font_size ?? 20}
            onChange={(v) => updateGamification({ result_code_box_font_size: v })}
            min={10}
            max={60}
            size="small"
          />
        </InputBlock>
      </ElementListCard>

    </CollapsedPanel>

    <CollapsedPanel title="Post Play / Winning / Losing Screen" open={false} allowed>
      <ResultElementManager gamification={gamification} updateGamification={updateGamification} />

      <ElementListCard>
        <ElementListHeader>Layout &amp; Spacing</ElementListHeader>
        <InputBlock label="Result screen layout">
          <SelectInput
            items={resultLayoutOptions}
            value={gamification.result_layout || 'stacked'}
            onChange={(value) => updateGamification({ result_layout: value })}
          />
        </InputBlock>
        <InputBlock label="Result screen element spacing (px)">
          <NumberInput
            value={gamification.result_element_gap ?? 8}
            onChange={(v) => updateGamification({ result_element_gap: v })}
            min={0}
            max={40}
            size="small"
          />
        </InputBlock>
      </ElementListCard>

      <ElementListCard>
        <ElementListHeader>Typography</ElementListHeader>
        <InputBlock label="Result headline font size (px)">
          <NumberInput
            value={gamification.result_headline_font_size ?? 34}
            onChange={(v) => updateGamification({ result_headline_font_size: v })}
            min={12}
            max={72}
            size="small"
          />
        </InputBlock>
        <InputBlock label="Result caption font size (px)">
          <NumberInput
            value={gamification.result_caption_font_size ?? 17}
            onChange={(v) => updateGamification({ result_caption_font_size: v })}
            min={10}
            max={40}
            size="small"
          />
        </InputBlock>
        <InputBlock label='"You won X" font size (px)'>
          <NumberInput
            value={gamification.result_reward_line_font_size ?? 29}
            onChange={(v) => updateGamification({ result_reward_line_font_size: v })}
            min={12}
            max={60}
            size="small"
          />
        </InputBlock>

        {/* Result screen CTA button typography (replaces old font-guessing) */}
        <InputBlock label="Result CTA button font">
          <SelectInput
            items={ctaFontFamilyOptions}
            value={gamification.result_cta_font_family || ''}
            onChange={(value) => updateGamification({ result_cta_font_family: value || undefined })}
          />
        </InputBlock>
        <InputBlock label="Result CTA button font size (px)">
          <NumberInput
            value={gamification.result_cta_font_size ?? ''}
            onChange={(v) => updateGamification({ result_cta_font_size: v || undefined })}
            min={8}
            max={48}
            size="small"
          />
        </InputBlock>
      </ElementListCard>

      <ElementListCard>
        <ElementListHeader>Reveal Effects</ElementListHeader>
        <InputBlock
          label="Reveal animation"
          helpText="Staggered: elements pop in one by one. All at once: the classic fade-in."
        >
          <SelectInput
            items={revealAnimationOptions}
            value={gamification.result_reveal_animation || 'staggered'}
            onChange={(value) => updateGamification({ result_reveal_animation: value })}
          />
        </InputBlock>
        <InputBlock
          label="Confetti"
          helpText="When on, confetti colors automatically match your popup's brand colors."
        >
          <SelectInput
            items={confettiOptions}
            value={gamification.result_confetti || 'auto'}
            onChange={(value) => updateGamification({ result_confetti: value })}
          />
        </InputBlock>
      </ElementListCard>

    </CollapsedPanel>
    </>
  );
}

export default GamificationPanel;
